import * as THREE from 'three';
import { FORMATIONS_GLSL } from './formationsGLSL';

const FORMATION_COUNT = 14;

/**
 * WebGL2 GPGPU particle field — Immersiom-formation port of the WebGPU
 * `ParticleSystem`.
 *
 * State lives in THREE RGBA32F textures rotated each step. The pair
 * `(prevIdx, nowIdx)` is sampled and the next position is written into
 * `writeIdx`, which is never one of the sampled slots — otherwise WebGL
 * raises a framebuffer-vs-texture feedback loop error and the draw is
 * dropped. After writing we rotate: `(prev, now, write) <- (now, write, prev)`.
 */
export class ParticleSystemGL {
  readonly mesh: THREE.Points;
  readonly count: number;
  readonly texSize: number;

  private posTargets: [THREE.WebGLRenderTarget, THREE.WebGLRenderTarget, THREE.WebGLRenderTarget];
  private prevIdx = 0;
  private nowIdx = 1;
  private writeIdx = 2;

  private fsScene = new THREE.Scene();
  private fsCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private fsQuad: THREE.Mesh;

  private initMat: THREE.ShaderMaterial;
  private updateMat: THREE.ShaderMaterial;
  private renderMat: THREE.ShaderMaterial;

  // Audio + state uniforms shared between update and render.
  private uTime = { value: 0 };
  private uDt = { value: 1 / 60 };
  private uBass = { value: 0 };
  private uMid = { value: 0 };
  private uTreble = { value: 0 };
  private uBeat = { value: 0 };
  private uFormationA = { value: 0 };
  private uFormationB = { value: 0 };
  private uFormationMix = { value: 0 };
  private uBaseR = { value: 8.0 };
  private uBrightness = { value: 0.62 };

  // Crossfade controller state.
  private currentFormation = 0;
  private crossfadeFrom = 0;
  private crossfadeTo = 0;
  private crossfadeT = 1;
  private crossfadeDuration = 1.4;

  constructor(private renderer: THREE.WebGLRenderer, count: number) {
    this.count = count;
    this.texSize = Math.ceil(Math.sqrt(count));

    // We need RGBA32F render targets. WebGL2 + EXT_color_buffer_float is
    // ubiquitous on desktop/mobile; we don't gate on it because three.js
    // surfaces a clear error if the extension is missing.
    const targetOpts: THREE.RenderTargetOptions = {
      type: THREE.FloatType,
      format: THREE.RGBAFormat,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      wrapS: THREE.ClampToEdgeWrapping,
      wrapT: THREE.ClampToEdgeWrapping,
      depthBuffer: false,
      stencilBuffer: false,
      generateMipmaps: false,
    };
    this.posTargets = [
      new THREE.WebGLRenderTarget(this.texSize, this.texSize, targetOpts),
      new THREE.WebGLRenderTarget(this.texSize, this.texSize, targetOpts),
      new THREE.WebGLRenderTarget(this.texSize, this.texSize, targetOpts),
    ];

    this.initMat = this.buildInitMaterial();
    this.updateMat = this.buildUpdateMaterial();
    this.renderMat = this.buildRenderMaterial();

    this.fsQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.initMat);
    this.fsScene.add(this.fsQuad);

    this.mesh = this.buildPoints();
  }

  init(): void {
    this.fsQuad.material = this.initMat;
    // Seed all three slots with the same Fibonacci rest position so the
    // implied velocity at t=0 is zero.
    for (let i = 0; i < 3; i++) {
      this.renderer.setRenderTarget(this.posTargets[i]);
      this.renderer.render(this.fsScene, this.fsCam);
    }
    this.renderer.setRenderTarget(null);
    // Point the render material at the freshest history pair (matches the
    // post-step state we'd land in after one cycle).
    this.renderMat.uniforms.uPosTex.value = this.posTargets[this.nowIdx].texture;
    this.renderMat.uniforms.uPosPrevTex.value = this.posTargets[this.prevIdx].texture;
  }

  step(dt: number, time: number): void {
    // Fixed substep — keeps the spring stable even when the host loop
    // hiccups. Matches the WebGPU engine's behavior.
    this.uTime.value = time;
    this.uDt.value = 1 / 60;

    // Bind: read prev + now, write into the disjoint third slot. The
    // disjoint write is what avoids the feedback-loop GL error.
    this.updateMat.uniforms.uPosPrev.value = this.posTargets[this.prevIdx].texture;
    this.updateMat.uniforms.uPosNow.value = this.posTargets[this.nowIdx].texture;

    this.fsQuad.material = this.updateMat;
    this.renderer.setRenderTarget(this.posTargets[this.writeIdx]);
    this.renderer.render(this.fsScene, this.fsCam);
    this.renderer.setRenderTarget(null);

    // Rotate (prev, now, write) <- (now, write, prev).
    const newPrev = this.nowIdx;
    const newNow = this.writeIdx;
    const newWrite = this.prevIdx;
    this.prevIdx = newPrev;
    this.nowIdx = newNow;
    this.writeIdx = newWrite;

    // Wire the render mesh to the new textures.
    this.renderMat.uniforms.uPosTex.value = this.posTargets[this.nowIdx].texture;
    this.renderMat.uniforms.uPosPrevTex.value = this.posTargets[this.prevIdx].texture;

    void dt;
  }

  setAudio(bass: number, mid: number, treble: number, beat: number): void {
    this.uBass.value = bass;
    this.uMid.value = mid;
    this.uTreble.value = treble;
    this.uBeat.value = beat;
  }

  setFormation(id: number, durationSec = 1.4): void {
    const target = ((id % FORMATION_COUNT) + FORMATION_COUNT) % FORMATION_COUNT;
    if (target === this.currentFormation && this.crossfadeT >= 1) return;
    this.crossfadeFrom = this.currentFormation;
    this.crossfadeTo = target;
    this.crossfadeT = 0;
    this.crossfadeDuration = Math.max(0.05, durationSec);
    this.uFormationA.value = this.crossfadeFrom;
    this.uFormationB.value = this.crossfadeTo;
    this.uFormationMix.value = 0;
    this.currentFormation = target;
  }

  getFormation(): number {
    return this.currentFormation;
  }

  advanceCrossfade(dt: number): void {
    if (this.crossfadeT >= 1) {
      this.uFormationA.value = this.currentFormation;
      this.uFormationB.value = this.currentFormation;
      this.uFormationMix.value = 0;
      return;
    }
    this.crossfadeT += dt / this.crossfadeDuration;
    if (this.crossfadeT >= 1) {
      this.crossfadeT = 1;
      this.uFormationA.value = this.currentFormation;
      this.uFormationB.value = this.currentFormation;
      this.uFormationMix.value = 0;
    } else {
      const x = this.crossfadeT;
      this.uFormationMix.value = x * x * (3 - 2 * x); // smoothstep
    }
  }

  setBaseRadius(r: number): void {
    this.uBaseR.value = r;
  }

  setViewportHeight(h: number): void {
    this.renderMat.uniforms.uViewportHeight.value = h;
  }

  setPointSize(size: number): void {
    this.renderMat.uniforms.uPointSize.value = size;
  }

  setAlpha(a: number): void {
    this.renderMat.uniforms.uBaseAlpha.value = a;
  }

  setBrightness(value: number): void {
    this.uBrightness.value = value;
  }

  setStiffness(k: number): void {
    this.updateMat.uniforms.uStiff.value = k;
  }

  setDamping(d: number): void {
    this.updateMat.uniforms.uDamp.value = d;
  }

  dispose(): void {
    this.posTargets[0].dispose();
    this.posTargets[1].dispose();
    this.posTargets[2].dispose();
    this.initMat.dispose();
    this.updateMat.dispose();
    this.renderMat.dispose();
    (this.fsQuad.geometry as THREE.BufferGeometry).dispose();
    (this.mesh.geometry as THREE.BufferGeometry).dispose();
  }

  // ---------- internals ----------------------------------------------------

  private buildInitMaterial(): THREE.ShaderMaterial {
    const vertex = /* glsl */ `
      out vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = vec4(position.xy, 0.0, 1.0);
      }
    `;
    const fragment = /* glsl */ `
      precision highp float;
      in vec2 vUv;
      out vec4 outColor;
      uniform float uSize;
      uniform float uCount;
      uniform float uInvCount;
      uniform float uBaseR;
      ${FORMATIONS_GLSL}
      void main() {
        float px = floor(vUv.x * uSize);
        float py = floor(vUv.y * uSize);
        float i = py * uSize + px;
        if (i >= uCount) { outColor = vec4(0.0, 0.0, 0.0, 1.0); return; }
        vec3 rest = fibonacciRest(i, uInvCount);
        outColor = vec4(rest * uBaseR, 1.0);
      }
    `;
    return new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: vertex,
      fragmentShader: fragment,
      uniforms: {
        uSize: { value: this.texSize },
        uCount: { value: this.count },
        uInvCount: { value: 1 / Math.max(1, this.count - 1) },
        uBaseR: this.uBaseR,
      },
    });
  }

  private buildUpdateMaterial(): THREE.ShaderMaterial {
    const vertex = /* glsl */ `
      out vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = vec4(position.xy, 0.0, 1.0);
      }
    `;
    const fragment = /* glsl */ `
      precision highp float;
      in vec2 vUv;
      out vec4 outColor;
      uniform sampler2D uPosPrev;
      uniform sampler2D uPosNow;
      uniform float uSize;
      uniform float uCount;
      uniform float uInvCount;
      uniform float uBaseR;
      uniform float uTime;
      uniform float uDt;
      uniform float uBass;
      uniform float uMid;
      uniform float uTreble;
      uniform float uBeat;
      uniform int uFormationA;
      uniform int uFormationB;
      uniform float uFormationMix;
      uniform float uStiff;
      uniform float uDamp;
      ${FORMATIONS_GLSL}
      void main() {
        float px = floor(vUv.x * uSize);
        float py = floor(vUv.y * uSize);
        float i = py * uSize + px;
        if (i >= uCount) { outColor = vec4(0.0, 0.0, 0.0, 1.0); return; }

        vec3 rest = fibonacciRest(i, uInvCount);
        vec3 prev = texture(uPosPrev, vUv).rgb;
        vec3 now = texture(uPosNow, vUv).rgb;

        vec3 tA = dispatchFormation(uFormationA, rest, uTime, uBaseR, uBass, uMid, uTreble, uBeat);
        vec3 target;
        if (uFormationA == uFormationB) {
          target = tA;
        } else {
          vec3 tB = dispatchFormation(uFormationB, rest, uTime, uBaseR, uBass, uMid, uTreble, uBeat);
          target = mix(tA, tB, uFormationMix);
        }

        vec3 vel = (now - prev) / uDt;
        vec3 accel = (target - now) * uStiff - vel * uDamp;
        vec3 velNext = vel + accel * uDt;
        vec3 posNext = now + velNext * uDt;
        outColor = vec4(posNext, 1.0);
      }
    `;
    return new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: vertex,
      fragmentShader: fragment,
      uniforms: {
        uPosPrev: { value: null },
        uPosNow: { value: null },
        uSize: { value: this.texSize },
        uCount: { value: this.count },
        uInvCount: { value: 1 / Math.max(1, this.count - 1) },
        uBaseR: this.uBaseR,
        uTime: this.uTime,
        uDt: this.uDt,
        uBass: this.uBass,
        uMid: this.uMid,
        uTreble: this.uTreble,
        uBeat: this.uBeat,
        uFormationA: this.uFormationA,
        uFormationB: this.uFormationB,
        uFormationMix: this.uFormationMix,
        uStiff: { value: 28.0 },
        uDamp: { value: 10.5 },
      },
    });
  }

  private buildRenderMaterial(): THREE.ShaderMaterial {
    const vertex = /* glsl */ `
      in vec2 aRef;
      uniform sampler2D uPosTex;
      uniform sampler2D uPosPrevTex;
      uniform float uPointSize;
      uniform float uViewportHeight;
      out float vSpeed;
      void main() {
        vec3 pos = texture(uPosTex, aRef).rgb;
        vec3 posPrev = texture(uPosPrevTex, aRef).rgb;
        vSpeed = length(pos - posPrev);
        vec4 mv = modelViewMatrix * vec4(pos, 1.0);
        gl_Position = projectionMatrix * mv;
        // Perspective-scaled point size in pixels. The factor projects a
        // world-space size into screen pixels using the camera's vertical
        // FOV term (projectionMatrix[1][1] = cot(fov/2)) and the viewport
        // height. Floor at 1px so distant particles still rasterize.
        float dist = max(0.1, -mv.z);
        float pxSize = uPointSize * uViewportHeight * projectionMatrix[1][1] / dist;
        gl_PointSize = clamp(pxSize, 1.0, 32.0);
      }
    `;
    const fragment = /* glsl */ `
      precision highp float;
      in float vSpeed;
      out vec4 fragColor;
      uniform float uBass;
      uniform float uBeat;
      uniform float uDt;
      uniform float uBaseAlpha;
      uniform float uBrightness;
      void main() {
        vec2 c = gl_PointCoord - 0.5;
        float d2 = dot(c, c);
        if (d2 > 0.25) discard;
        float disc = smoothstep(0.25, 0.0, d2);
        vec3 cool = vec3(0.22, 0.42, 1.05);
        vec3 warm = vec3(1.04, 0.52, 0.82);
        float speedScaled = vSpeed / max(uDt, 0.001);
        float audioWarmth = uBass * 0.35 + uBeat * 0.35;
        float warmth = clamp(speedScaled * 0.6 + audioWarmth, 0.0, 1.0);
        vec3 tint = mix(cool, warm, warmth);
        float beatLift = 0.86 + uBeat * 0.38 + uBass * 0.18;
        fragColor = vec4(tint * beatLift * uBrightness, disc * uBaseAlpha);
      }
    `;
    return new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: vertex,
      fragmentShader: fragment,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uPosTex: { value: null },
        uPosPrevTex: { value: null },
        uPointSize: { value: 0.03 },
        uViewportHeight: { value: 800 },
        uDt: this.uDt,
        uBass: this.uBass,
        uBeat: this.uBeat,
        uBaseAlpha: { value: 0.42 },
        uBrightness: this.uBrightness,
      },
    });
  }

  private buildPoints(): THREE.Points {
    // One vertex per particle. We pass the texture lookup coord as a
    // vertex attribute. Position is unused (vertex shader replaces it
    // with the texture sample), but three.js requires a `position`
    // attribute to bind a BufferGeometry, so we provide a zero buffer.
    const geom = new THREE.BufferGeometry();
    const refs = new Float32Array(this.count * 2);
    const positions = new Float32Array(this.count * 3);
    const halfTexel = 0.5 / this.texSize;
    for (let i = 0; i < this.count; i++) {
      const px = i % this.texSize;
      const py = Math.floor(i / this.texSize);
      refs[i * 2 + 0] = px / this.texSize + halfTexel;
      refs[i * 2 + 1] = py / this.texSize + halfTexel;
    }
    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geom.setAttribute('aRef', new THREE.BufferAttribute(refs, 2));
    geom.setDrawRange(0, this.count);
    geom.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 100);

    const points = new THREE.Points(geom, this.renderMat);
    points.frustumCulled = false;
    return points;
  }
}
